import * as React from 'react';
import { cloneElement, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    AppBar as MuiAppBar,
    IconButton,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    Theme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import SettingsIcon from '@material-ui/icons/Settings';
import { UserMenu, MenuItemLink, useTranslate } from 'react-admin';
import { toggleSidebar } from 'ra-core';
import { HideOnScroll, LoadingIndicator } from 'ra-ui-materialui';
import Logo from './Logo';
import SupportRota from './SupportRota';
import { AppState } from '../types';

const useStyles = makeStyles((theme) => ({
    toolbar: {
        paddingRight: 24,
    },
    menuButton: {
        marginLeft: '0.5em',
        marginRight: '0.5em',
        backgroundColor: 'rgba(28, 30, 31, 0.06)',
        width: 40,
        height: 40,
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(218, 41, 114, 0.12)',
        },
    },
    menuButtonOpen: {
        backgroundColor: 'rgba(218, 41, 114, 0.12)',
        color: '#DA2972',
    },
    title: {
        flex: 1,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    spacer: {
        flex: 1,
    },
    logo: {
        color: '#DA2972',
        height: 50,
        width: 'auto',
        marginRight: 20,
        flexShrink: 0,
    },
    toggleIcon: {
        transition: theme.transitions.create(['transform', 'opacity'], {
            duration: theme.transitions.duration.shorter,
        }),
    },
}));

const ConfigurationMenu = forwardRef<any, any>((props, ref) => {
    const translate = useTranslate();
    return (
        <MenuItemLink
            ref={ref}
            to="/configuration"
            primaryText={translate('pos.configuration')}
            leftIcon={<SettingsIcon />}
            onClick={props.onClick}
            sidebarIsOpen
        />
    );
});

const CustomUserMenu = (props: any) => (
    <UserMenu {...props}>
        <ConfigurationMenu />
    </UserMenu>
);

const CustomAppBar = (props: any) => {
    const {
        className,
        color = 'secondary',
        logout,
        userMenu = <CustomUserMenu />,
        ...rest
    } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const isXSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
    const open = useSelector((state: AppState) => state.admin.ui.sidebarOpen);

    const toggleLabel = open ? 'Close sidebar' : 'Open sidebar';

    // Avoid forwarding react-admin Layout props that MUI AppBar doesn't accept
    const {
        open: _open,
        title: _title,
        userMenu: _userMenu,
        logout: _logout,
        ...muiAppBarProps
    } = rest;

    return (
        <HideOnScroll>
            <MuiAppBar className={className} color={color} elevation={1} {...muiAppBarProps}>
                <Toolbar
                    disableGutters
                    variant={isXSmall ? 'regular' : 'dense'}
                    className={classes.toolbar}
                >
                    <Tooltip title={toggleLabel} enterDelay={300}>
                        <IconButton
                            color="inherit"
                            onClick={() => dispatch(toggleSidebar())}
                            className={`${classes.menuButton} ${open ? classes.menuButtonOpen : ''}`}
                            aria-label={toggleLabel}
                            aria-expanded={open}
                        >
                            {open ? (
                                <ChevronLeftIcon className={classes.toggleIcon} />
                            ) : (
                                <MenuIcon className={classes.toggleIcon} />
                            )}
                        </IconButton>
                    </Tooltip>
                    <Logo className={classes.logo} />
                    <Typography
                        variant="h6"
                        color="inherit"
                        className={classes.title}
                        id="react-admin-title"
                    />
                    <span className={classes.spacer} />
                    <SupportRota />
                    <LoadingIndicator />
                    {cloneElement(userMenu, { logout })}
                </Toolbar>
            </MuiAppBar>
        </HideOnScroll>
    );
};

export default CustomAppBar;
